import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, SimpleChanges, WritableSignal, computed, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectOptionsConfig } from './select-options.interface';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, takeUntil } from 'rxjs';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';

@Component({
  selector: 'select-options',
  standalone: true,
  imports: [CommonModule, FormsModule, ScrollingModule, TranslateModule],
  templateUrl: './select-options.component.html',
  styleUrl: './select-options.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectOptionsComponent {
  @Input() config!: SelectOptionsConfig;
  @Output() selectionChange = new EventEmitter<{ values: any | any[], displayNames: string | string[] }>();

  options: WritableSignal<any[]> = signal([]);
  filteredOptions: WritableSignal<any[]> = signal([]);
  searchText: WritableSignal<string> = signal('');
  isLoading: WritableSignal<boolean> = signal(true);
  loadingMore: WritableSignal<boolean> = signal(false);
  error: WritableSignal<string | null> = signal(null);
  selectedItems: WritableSignal<any[]> = signal([]);
  selectedItem: WritableSignal<any | null> = signal(null);
  currentPage: WritableSignal<number> = signal(1);
  totalPages: WritableSignal<number> = signal(1);
  hasMorePages: WritableSignal<boolean> = signal(false);
  groupedOptionsMap: WritableSignal<Record<string, any>> = signal({});
  groupedOptionsList: WritableSignal<any[]> = signal([]);
  _markForCheckTimeout: any;
  private search$ = new Subject<{ params: Record<string, any>; isInitialLoad: boolean; preselectedData?: any[] }>();
  private destroy$ = new Subject<void>();
  private _searchTimeout: any;
  private _scrollTimeout: any;
  private _originalData: any[] = [];
  private signalsHash = computed(() =>
    JSON.stringify({
      options: this.options(),
      filteredOptions: this.filteredOptions(),
      searchText: this.searchText(),
      isLoading: this.isLoading(),
      loadingMore: this.loadingMore(),
      error: this.error(),
      selectedItems: this.selectedItems(),
      selectedItem: this.selectedItem(),
      currentPage: this.currentPage(),
      totalPages: this.totalPages(),
      hasMorePages: this.hasMorePages(),
      groupedOptionsMap: this.groupedOptionsMap(),
      groupedOptionsList: this.groupedOptionsList(),
    })
  );
  constructor(
    private dataCollectorService: DataCollectorService,
    private cdr: ChangeDetectorRef,
    private localizationService: LocalisationService,
    private utilityService: UtilityService
  ) {
    this.setupReactivity();
  }

  ngOnInit(): void {
    this.localizationService.setDefaultLanguage();
    this.setupSearchStream();
    this.initializeComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && !changes['config'].firstChange) {
      this.initializeComponent();
    }
  }

  ngOnDestroy(): void {
    if (this._searchTimeout) {
      clearTimeout(this._searchTimeout);
    }
    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
    }
    if (this._markForCheckTimeout) {
      clearTimeout(this._markForCheckTimeout);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupReactivity(): void {
    effect(() => {
      this.signalsHash();
      if (this._markForCheckTimeout) {
        clearTimeout(this._markForCheckTimeout);
      }
      this._markForCheckTimeout = setTimeout(() => {
        this.cdr.detectChanges();
      }, 50);
    });
  }

  initializeComponent(): void {
    this.isLoading.set(true);
    this.currentPage.set(1);
    this.options.set([]);
    this.filteredOptions.set([]);
    this.groupedOptionsMap.set({});
    this.groupedOptionsList.set([]);
    this._originalData = [];
    this.error.set(null);
    const params: Record<string, any> = { ...this.config?.filterParams || {} };
    const preselectedValues = this.config?.preselectedValues || [];
    if (preselectedValues.length > 0 && this.config?.prefillFilterParam) {
      params[this.config.prefillFilterParam] = preselectedValues.join(',');
    }
    this.fetchData(params, true);
  }

  setupSearchStream(): void {
    this.search$.pipe(
      debounceTime(300),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      switchMap(({ params, isInitialLoad, preselectedData = [] }) => {
        if (isInitialLoad) {
          this.isLoading.set(true);
          this.loadingMore.set(false);
        } else {
          this.loadingMore.set(true);
          params['page'] = this.currentPage();
        }
        if (this.config.apiSearch && this.config.keywordSearchParam && this.searchText()) {
          params[this.config.keywordSearchParam] = this.searchText();
        }
        return this.dataCollectorService.getData(this.config?.url, params).pipe(
          catchError(err => {
            this.error.set(this.utilityService.getLocaleValue('error_occurred.while_loading_data', 'Error occurred while loading data'));
            return of(null);
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (response) {
          this.handleFetchResponse(response, this.search$.observers.length > 0);
        }
        this.isLoading.set(false);
        this.loadingMore.set(false);
      },
      error: () => {
        this.error.set(this.utilityService.getLocaleValue('error_occurred.while_loading_data', 'Error occurred while loading data'));
        this.isLoading.set(false);
        this.loadingMore.set(false);
      }
    });
  }

  fetchData(params: any, isInitialLoad = false): void {
    const preselectedValues = this.config?.preselectedValues || [];

    if (isInitialLoad && preselectedValues.length > 0 && this.config?.prefillFilterParam) {
      const preselectedParams = { ...params };
      preselectedParams[this.config.prefillFilterParam] = preselectedValues.join(',');

      this.dataCollectorService.getData(this.config?.url, preselectedParams).subscribe({
        next: (response: any) => {
          try {
            let data = response;
            if (this.config?.dataPath) {
              const paths = this.config.dataPath.split('.');
              for (const path of paths) {
                if (data && data[path] !== undefined) {
                  data = data[path];
                } else {
                  break;
                }
              }
            }

            if (Array.isArray(data)) {
              this._originalData = [...data];
              const processedData = this.processNestedArrays(data);
              this.handlePreselection(processedData);

              const firstPageParams = { ...params };
              if (this.config?.prefillFilterParam) {
                delete firstPageParams[this.config.prefillFilterParam];
              }

              this.search$.next({
                params: firstPageParams,
                isInitialLoad: true,
                preselectedData: processedData
              });
            }
          } catch (e) {
            this.error.set(this.utilityService.getLocaleValue('status.sync_history.error', 'Error'));
            this.search$.next({ params, isInitialLoad: true });
          }
        },
        error: (err) => {
          this.error.set(this.utilityService.getLocaleValue('error_occurred.while_loading_data', 'Error occurred while loading data'));
          this.search$.next({ params, isInitialLoad: true });
        }
      });
    } else {
      this.search$.next({ params, isInitialLoad });
    }
  }

  handleFetchResponse(response: any, hasPreselectedData: boolean): void {
    try {
      let data = response;
      if (this.config?.dataPath) {
        const paths = this.config.dataPath.split('.');
        for (const path of paths) {
          if (data && data[path] !== undefined) {
            data = data[path];
          } else {
            this.error.set(this.utilityService.getLocaleValue('status.sync_history.error', 'Error'));
            return;
          }
        }
      }

      if (Array.isArray(data)) {
        const isInitialLoad = this.options().length === 0;

        if (isInitialLoad) {
          this._originalData = [...data];
        } else {
          const keyUid = this.config.keyUid;
          const existingIds = this._originalData.map(item => item[keyUid]);
          const uniqueNewItems = data.filter(item => !existingIds.includes(item[keyUid]));
          this._originalData = [...this._originalData, ...uniqueNewItems];
        }

        const processedData = this.processNestedArrays(data);

        if (isInitialLoad) {
          this.options.set(processedData);
          this.filteredOptions.set(processedData);
          if (!hasPreselectedData) {
            this.handlePreselection(processedData);
          }
        } else {
          const keyUid = this.config.keyUid;
          const currentOptionIds = this.options().map(item => item[keyUid]);
          const uniqueNewOptions = processedData.filter(item =>
            !currentOptionIds.includes(item[keyUid])
          );
          this.options.set([...this.options(), ...uniqueNewOptions]);
          this.filteredOptions.set([...this.options()]);
        }

        if (this.config?.groupByKey) {
          this.updateGroupedOptions();
        }

        const totalPages = response.total_pages || response.totalPages ||
          (response.pagination?.total_pages) || (response.meta?.total_pages) || 1;
        this.totalPages.set(totalPages);
        this.hasMorePages.set(this.currentPage() < totalPages);
      } else {
        this.error.set(this.utilityService.getLocaleValue('status.sync_history.error', 'Error'));
      }
    } catch (e) {
      this.error.set(this.utilityService.getLocaleValue('error_occurred.while_loading_data', 'Error occurred while loading data'));
    }
  }

  fetchFirstPage(params: any, preselectedData: any[] = []): void {
    if (this.config?.prefillFilterParam) {
      delete params[this.config.prefillFilterParam];
    }

    this.performFetch(params, true, preselectedData);
  }

  performFetch(params: any, isInitialLoad = false, preselectedData: any[] = []): void {
    this.dataCollectorService.getData(this.config?.url, params).subscribe({
      next: (response: any) => {
        try {
          let data = response;
          if (this.config?.dataPath) {
            const paths = this.config.dataPath.split('.');
            for (const path of paths) {
              if (data && data[path] !== undefined) {
                data = data[path];
              } else {
                this.error.set(this.utilityService.getLocaleValue('status.sync_history.error', 'Error'));
                break;
              }
            }
          }

          if (Array.isArray(data)) {
            if (preselectedData.length > 0) {
              const keyUid = this.config.keyUid;
              const newItems = data.filter(item => 
                !preselectedData.some(preItem => preItem[keyUid] === item[keyUid])
              );
              data = [...preselectedData, ...newItems];
            }

            if (isInitialLoad) {
              this._originalData = [...data];
            } else {
              const keyUid = this.config.keyUid;
              const existingIds = this._originalData.map(item => item[keyUid]);
              const uniqueNewItems = data.filter(item => !existingIds.includes(item[keyUid]));
              this._originalData = [...this._originalData, ...uniqueNewItems];
            }

            const processedData = this.processNestedArrays(data);

            if (isInitialLoad) {
              this.options.set(processedData);
              this.filteredOptions.set(processedData);
              if (preselectedData.length === 0) {
                this.handlePreselection(processedData);
              }
            } else {
              const keyUid = this.config.keyUid;
              const currentOptionIds = this.options().map(item => item[keyUid]);
              const uniqueNewOptions = processedData.filter(item => 
                !currentOptionIds.includes(item[keyUid])
              );
              this.options.set([...this.options(), ...uniqueNewOptions]);
              this.filteredOptions.set([...this.options()]);
            }

            if (this.config?.groupByKey) {
              this.updateGroupedOptions();
            }

            const totalPages = response.total_pages || response.totalPages ||
              (response.pagination?.total_pages) || (response.meta?.total_pages) || 1;
            this.totalPages.set(totalPages);
            this.hasMorePages.set(this.currentPage() < totalPages);
          } else {
            this.error.set(this.utilityService.getLocaleValue('status.sync_history.error', 'Error'));
          }
        } catch (e) {
          this.error.set(this.utilityService.getLocaleValue('error_occurred.while_loading_data', 'Error occurred while loading data'));
        } finally {
          this.isLoading.set(false);
          this.loadingMore.set(false);
        }
      },
      error: (err) => {
        this.error.set(this.utilityService.getLocaleValue('error_occurred.while_loading_data', 'Error occurred while loading data'));
        this.isLoading.set(false);
        this.loadingMore.set(false);
      }
    });
  }

  processNestedArrays(data: any[]): any[] {
    if (!this.config.nestedArrayPath) {
      return data;
    }
    const result: any[] = [];
    for (const item of data) {
      const nestedArray = item[this.config.nestedArrayPath];
      if (Array.isArray(nestedArray)) {
        for (const nestedItem of nestedArray) {
          const processedItem = { ...nestedItem };
          if (this.config.groupFromParent && this.config.groupByKey) {
            if (this.config.groupByKey && item[this.config.groupByKey] !== undefined) {
              processedItem[this.config.groupByKey] = item[this.config.groupByKey];
            }
            if (this.config.groupNameKey && item[this.config.groupNameKey] !== undefined) {
              processedItem[this.config.groupNameKey] = item[this.config.groupNameKey];
            }
            processedItem['_parentData'] = item;
          }
          result.push(processedItem);
        }
      }
    }
    return result.length > 0 ? result : data;
  }

  updateGroupedOptions(): void {
    if (!this.config?.groupByKey) return;
    const groupMap: Record<string, any> = {};
    const filteredItems = this.filteredOptions();
    const groupNameMap: Record<string, string> = {};
    if (this.config?.groupNameKey) {
      for (const item of filteredItems) {
        const groupValue = item[this.config?.groupByKey];
        const groupKey = String(groupValue ?? 'ungrouped');
        if (!groupNameMap[groupKey] && item[this.config?.groupNameKey]) {
          groupNameMap[groupKey] = item[this.config?.groupNameKey];
        }
        if (!groupNameMap[groupKey] && this.config.groupFromParent) {
          const parentItem = this._originalData.find(parent => 
            parent[this.config.groupByKey!] === groupValue
          );
          if (parentItem && parentItem[this.config.groupNameKey!]) {
            groupNameMap[groupKey] = parentItem[this.config.groupNameKey!];
          }
        }
      }
    }
    for (const item of filteredItems) {
      const groupValue = item[this.config?.groupByKey];
      const groupKey = String(groupValue ?? 'ungrouped');
      if (!groupMap[groupKey]) {
        let groupLabel: string;
        if (this.config?.groupNameKey && groupNameMap[groupKey]) {
          groupLabel = groupNameMap[groupKey];
        } else {
          groupLabel = String(groupValue ?? 'Ungrouped');
        }
        groupMap[groupKey] = { 
          groupValue: groupValue, 
          groupLabel: groupLabel, 
          items: [] 
        };
      }
      groupMap[groupKey].items.push(item);
    }
    this.groupedOptionsMap.set(groupMap);
    this.groupedOptionsList.set(Object.values(groupMap).sort((a, b) => 
      a.groupLabel.localeCompare(b.groupLabel)
    ));
  }

  handlePreselection(data: any[]): void {
    if (this.config.preselectedValues?.length) {
      const preselectedUids = this.config.preselectedValues;
      if (this.config.multiSelect) {
        const preselectedItems = data.filter(item => 
          preselectedUids.includes(item[this.config.keyUid])
        );
        if (preselectedItems.length) {
          this.selectedItems.set(preselectedItems);
        }
      } else if (preselectedUids.length > 0) {
        const selectedItem = data.find(item => 
          item[this.config.keyUid] === preselectedUids[0]
        );
        if (selectedItem) {
          this.selectedItem.set(selectedItem);
        }
      }
      this.emitSelectionChange();
    }
  }

  loadMore(): void {
    if (this.hasMorePages() && !this.loadingMore() && !this.isLoading()) {
      this.currentPage.set(this.currentPage() + 1);
      const params = { ...this.config.filterParams || {} };
      if (this.config.apiSearch && this.config.keywordSearchParam && this.searchText()) {
        params[this.config.keywordSearchParam] = this.searchText();
      }
      this.search$.next({ params, isInitialLoad: false });
    }
  }

  onOptionsScroll(event: Event): void {
    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
    }
    this._scrollTimeout = setTimeout(() => {
      const target = event.target as HTMLElement;
      if (!target) return;
      const scrollHeight = target.scrollHeight;
      const scrollTop = target.scrollTop;
      const clientHeight = target.clientHeight;
      const scrollPosition = scrollTop + clientHeight;
      const threshold = scrollHeight * 0.80;
      if (scrollPosition >= threshold) {
        if (this.hasMorePages() && !this.loadingMore() && !this.isLoading()) {
          this.loadMore();
        }
      }
    }, 100);
  }

  onSearch(text: string): void {
    this.searchText.set(text);
    if (this._searchTimeout) {
      clearTimeout(this._searchTimeout);
    }
    if (this.config.apiSearch) {
      this._searchTimeout = setTimeout(() => {
        this.performApiSearch(text);
      }, 300);
    } else {
      this.applySearchFilter();
    }
  }

  
  performApiSearch(text: string): void {
    this.currentPage.set(1);
    this.options.set([]);
    this.filteredOptions.set([]);
    this.groupedOptionsMap.set({});
    this.groupedOptionsList.set([]);
    this._originalData = [];
    this.hasMorePages.set(false);
    this.totalPages.set(1);
    const params: Record<string, any> = { ...this.config.filterParams || {} };
    if (this.config.keywordSearchParam) {
      if (text && text.trim()) {
        params[this.config.keywordSearchParam] = text;
      } else {
        delete params[this.config.keywordSearchParam];
      }
    }
    this.search$.next({ params, isInitialLoad: true });
  }

  applySearchFilter(): void {
    const searchText = this.searchText();
    if (!searchText.trim()) {
      this.filteredOptions.set([...this.options()]);
    } else {
      const filtered = this.options().filter(option => {
        const searchLower = searchText.toLowerCase();
        let nameMatch = false;
        if (typeof this.config.nameKey === 'string') {
          const value = String(option[this.config.nameKey] || '');
          nameMatch = value.toLowerCase().includes(searchLower);
        } else if (Array.isArray(this.config.nameKey)) {
          nameMatch = this.config.nameKey.some(key => {
            const value = String(option[key] || '');
            return value.toLowerCase().includes(searchLower);
          });
        }

        if (nameMatch) return true;
        if (this.config.groupByKey) {
          if (this.config.groupNameKey && option[this.config.groupNameKey]) {
            const groupName = String(option[this.config.groupNameKey]).toLowerCase();
            if (groupName.includes(searchLower)) return true;
          }
          if (this.config.groupFromParent && option['_parentData'] &&
            this.config.groupNameKey && option['_parentData'][this.config.groupNameKey]) {
            const parentGroupName = String(option['_parentData'][this.config.groupNameKey]).toLowerCase();
            if (parentGroupName.includes(searchLower)) return true;
          }
          if (option[this.config.groupByKey]) {
            const groupValue = String(option[this.config.groupByKey]).toLowerCase();
            if (groupValue.includes(searchLower)) return true;
          }
        }

        return false;
      });
      this.filteredOptions.set(filtered);
    }
    if (this.config.groupByKey) {
      this.updateGroupedOptions();
    }
  }

  clearSearch(): void {
    this.searchText.set('');

    if (this.config.apiSearch) {
      this.initializeComponent();
    } else {
      this.filteredOptions.set([...this.options()]);
      if (this.config.groupByKey) {
        this.updateGroupedOptions();
      }
    }
  }

  toggleSelection(option: any): void {
    if (this.config.multiSelect) {
      const currentItems = [...this.selectedItems()];
      const index = currentItems.findIndex(item => 
        item[this.config.keyUid] === option[this.config.keyUid]
      );
      if (index === -1) {
        currentItems.push(option);
      } else {
        currentItems.splice(index, 1);
      }
      this.selectedItems.set(currentItems);
    } else {
      this.selectedItem.set(option);
    }
    this.emitSelectionChange();
  }

  getItemDisplayName(item: any): string {
    if (!item || !this.config.nameKey) return '';
    if (typeof this.config.nameKey === 'string') {
      return item[this.config.nameKey] || '';
    }
    if (Array.isArray(this.config.nameKey)) {
      const separator = this.config.nameKeySeparator || ' ';
      return this.config.nameKey
        .map(key => item[key] || '')
        .filter(val => val)
        .join(separator);
    }
    return '';
  }
  
  getItemDisplayNameWithGroup(item: any): string {
    let displayName = this.getItemDisplayName(item);
    if (this.config.showGroupNameInDisplay && this.config.groupByKey && item[this.config.groupByKey] !== undefined) {
      let groupName = '';
      
      if (this.config.groupNameKey && item[this.config.groupNameKey]) {
        groupName = item[this.config.groupNameKey];
      } else if (this.config.groupFromParent && item['_parentData'] && item['_parentData'][this.config.groupNameKey!]) {
        groupName = item['_parentData'][this.config.groupNameKey!];
      } else {
        groupName = String(item[this.config.groupByKey]);
      }
      
      if (groupName) {
        displayName += ` (${groupName})`;
      }
    }
    
    return displayName;
  }

  emitSelectionChange(): void {
    if (this.config.multiSelect) {
      const selectedValues = this.selectedItems().map(item => item[this.config.keyUid]);
      const selectedNames = this.selectedItems().map(item => this.getItemDisplayNameWithGroup(item));
      this.selectionChange.emit({ values: selectedValues, displayNames: selectedNames });
    } else {
      const selected = this.selectedItem();
      this.selectionChange.emit({ 
        values: selected ? selected[this.config.keyUid] : null, 
        displayNames: selected ? this.getItemDisplayNameWithGroup(selected) : null 
      });
    }
  }

  isSelected(option: any): boolean {
    if (this.config.multiSelect) {
      return this.selectedItems().some(item => 
        item[this.config.keyUid] === option[this.config.keyUid]
      );
    } else {
      const selected = this.selectedItem();
      return selected !== null && selected[this.config.keyUid] === option[this.config.keyUid];
    }
  }

  clearAll(): void {
    if (this.config.multiSelect) {
      this.selectedItems.set([]);
    } else {
      this.selectedItem.set(null);
    }
    this.emitSelectionChange();
  }

  trackByFn(index: number, item: any): any {
    return item[this.config.keyUid];
  }

  trackByGroupFn(index: number, group: any): any {
    return group.groupValue;
  }
}